/** @jsxImportSource @emotion/react */
import { css, keyframes } from '@emotion/react';
import { useState, useEffect } from 'react';
import { withMyTheme } from "../theme/theme";
import { Menu as MenuIcon, Close as CloseIcon } from '@mui/icons-material';
import { IconButton } from '@mui/material';
import { SocialMediaIcons } from '../landing/footer/SocialMediaIcons';
import { useTranslation } from 'react-i18next';
import { NavItems } from './Navigation';
import { LanguageSelector } from '../components/LanguageSelector';

export const HAMBURGER_Z_INDEX = 1000;

const MenuButtonStyle = withMyTheme((theme, isExpanded: boolean) => css`
    position: fixed;
    top: 20px;
    right: 5vw;
    z-index: ${HAMBURGER_Z_INDEX};
    color: ${isExpanded ? theme.palette.common.white : theme.palette.primary.main};
    
    /* Style the ripple effect using theme colors */
    .MuiTouchRipple-child {
        background-color: ${theme.palette.primary.main};
    }
`);

const fadeIn = keyframes`
    from { opacity: 0; }
    to { opacity: 1; }
`;

const fadeOut = keyframes`
    from { opacity: 1; }
    to { opacity: 0; }
`;

const slideIn = keyframes`
    from { transform: translateX(100%); }
    to { transform: translateX(0); }
`;

const slideOut = keyframes`
    from { transform: translateX(0); }
    to { transform: translateX(100%); }
`;

const OverlayStyle = withMyTheme((theme, { isClosing }: { isClosing: boolean }) => css`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.8);
    z-index: 100;
    display: flex;
    justify-content: center;
    align-items: center;
    animation: ${isClosing ? fadeOut : fadeIn} 0.3s ease-out;
    animation-fill-mode: forwards;
`);

const MenuContainerStyle = withMyTheme((theme, { isClosing }: { isClosing: boolean }) => css`
    display: flex;
    flex-direction: column;
    gap: 2vh;
    text-align: center;
    animation: ${isClosing ? slideOut : slideIn} 0.3s ease-out;
    animation-fill-mode: forwards;
`);

const MenuItemStyle = withMyTheme((theme) => css`
    color: ${theme.palette.common.white};
    text-decoration: none;
    font-family: ${theme.typography.body1.fontFamily};
    font-size: 24px;
    font-weight: 600;
    cursor: pointer;
    transition: opacity 0.3s;
    padding: 10px;

    &:hover {
        opacity: 0.8;
    }
`);

const SocialMediaIconsStyle = withMyTheme(() => css`
    margin-top: 2vh;
`);

const LanguageSelectorContainerStyle = withMyTheme(() => css`
    margin-bottom: 1vh;
    display: flex;
    justify-content: center;
`);

export const NavigationMobile = () => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isClosing, setIsClosing] = useState(false);
    const [showMenu, setShowMenu] = useState(false);
    
    const { t } = useTranslation();

    // Handle menu open/close with animations
    useEffect(() => {
        if (isMenuOpen) {
            setIsClosing(false);
            setShowMenu(true);
        } else if (showMenu) {
            setIsClosing(true);
            const timer = setTimeout(() => {
                setShowMenu(false);
            }, 300); // Match animation duration
            return () => clearTimeout(timer);
        }
    }, [isMenuOpen]);

    const toggleMenu = () => {
        setIsMenuOpen(!isMenuOpen);
    };

    const scrollToSection = (id: string) => {
        const element = document.getElementById(id);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
            setIsMenuOpen(false);
        }
    };

    return (
        <>
            <IconButton 
                css={theme => MenuButtonStyle(theme, isMenuOpen)}
                onClick={toggleMenu}
                aria-label={isMenuOpen ? "Close menu" : "Open menu"}
                disableRipple={false}
                centerRipple={true}
                touchRippleRef={undefined}
            >
                {isMenuOpen ? <CloseIcon fontSize="large" /> : <MenuIcon fontSize="large" />}
            </IconButton>

            {showMenu && (
                <div css={theme => OverlayStyle(theme, { isClosing })}>
                    <nav css={theme => MenuContainerStyle(theme, { isClosing })}>
                        {NavItems.map((item) => (
                            <span key={item.id} css={MenuItemStyle} onClick={() => scrollToSection(item.id)}>{item.getText(t)}</span>
                        ))}
                        <div css={LanguageSelectorContainerStyle}>
                            <LanguageSelector />
                        </div>
                        <div css={SocialMediaIconsStyle}>
                            <SocialMediaIcons negative={true}/>
                        </div>
                    </nav>
                </div>
            )}
        </>
    );
};
